import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import axiosInstance from '../../../utils/axios';
import { languagesList } from '../../../utils/languages/languagesList';
interface Translation {
  [section: string]: { [key: string]: string };
}

function getRequestType(type: string): string {
  switch (type) {
    case 'email':
      return 'email';
    case 'notifications':
      return 'notification';
    case 'landing':
      return 'landing';
    case 'app':
    default:
      return 'ui';
  }
}

function getLanguageJsonUrl(type: string, language: string): string {
  switch (type) {
    case 'email':
      return `https://email-language.talkpal.ai/${language}.json`;
    case 'notifications':
      return `https://notification-language.talkpal.ai/${language}.json`;
    case 'landing':
      return `https://landing-language.talkpal.ai/${language}.json`;
    case 'app':
    default:
      return `https://language.talkpal.ai/${language}.json`;
  }
}

const TranslationManager: React.FC<{
  language: string;
  search: string;
  type?: 'app' | 'email' | 'notifications' | 'landing';
}> = ({ language, search, type = 'app' }) => {
  const isDescriptionMode = language === 'descriptions';
  const [newKey, setNewKey] = useState<string>('');
  const [newValue, setNewValue] = useState<string>('');
  const [translations, setTranslations] = useState<Translation>({});

  const { data } = useQuery({
    retryDelay: 0,
    retry: 0,
    queryKey: ['translations', type, language],
    queryFn: async () => {
      // custom url to fetch translations
      let langURL;
      switch (type) {
        case 'email':
          langURL = `https://email-language.talkpal.ai/${language}.json`;
          break;
        case 'notifications':
          langURL = `https://notification-language.talkpal.ai/${language}.json`;
          break;
        case 'landing':
          langURL = `https://landing-language.talkpal.ai/${language}.json`;
          break;
        case 'app':
        default:
          langURL = `https://language.talkpal.ai/${language}.json`;
          break;
      }
      const jsonData = await fetch(langURL, {
        cache: 'no-cache',
      }).then((res) => res.json());

      // Handling different json structure
      if (Object.keys(jsonData)?.[0] !== 'Common') {
        return { Common: jsonData };
      }

      return jsonData as Translation;
    },
  });
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      locale = 'en',
      translation,
    }: {
      locale: string;
      translation: Translation;
    }) => {
      let langURL;
      switch (type) {
        case 'email':
          langURL = `${process.env.REACT_APP_BASE_URL}/s3/update-interface-language/${locale}/email`;
          break;
        case 'notifications':
          langURL = `${process.env.REACT_APP_BASE_URL}/s3/update-interface-language/${locale}/notification`;
          break;
        case 'landing':
          langURL = `${process.env.REACT_APP_BASE_URL}/s3/update-interface-language/${locale}/landing`;
          break;
        case 'app':
        default:
          langURL = `${process.env.REACT_APP_BASE_URL}/s3/update-interface-language/${locale}/ui`;
          break;
      }
      const resp = await axiosInstance.put(langURL, {
        jsonBody: JSON.stringify(translation),
      });
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['translations'],
      });
    },
  });

  const { mutate: saveDescription, isPending: isSavingDescription } = useMutation({
    mutationFn: async ({ translation }: { translation: Translation }) => {
      const resp = await axiosInstance.put('/s3/key-description', {
        type: getRequestType(type),
        jsonBody: JSON.stringify(translation),
      });
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['translations'],
      });
    },
  });

  const { mutate: translateKey, isPending: isTranslating } = useMutation({
    mutationFn: async ({ key, type }: { key: string; type: string }) => {
      let requestType: string;
      switch (type) {
        case 'email':
          requestType = 'email';
          break;
        case 'notifications':
          requestType = 'notification';
          break;
        case 'landing':
          requestType = 'landing';
          break;
        case 'app':
        default:
          requestType = 'ui';
          break;
      }

      const resp = await axiosInstance.post('/s3/translate-interface-keys', {
        key,
        type: requestType,
      });

      alert(JSON.stringify(resp.data) || 'Key translation successful!');
      queryClient.invalidateQueries({ queryKey: ['translations'] });
      return resp.data;
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Failed to translate key.');
    },
  });

  const { mutate: copyEngValues, isPending: isCopying } = useMutation({
    mutationFn: async ({ key, type }: { key: string; type: string }) => {
      let requestType: string;
      switch (type) {
        case 'email':
          requestType = 'email';
          break;
        case 'notifications':
          requestType = 'notification';
          break;
        case 'landing':
          requestType = 'landing';
          break;
        case 'app':
        default:
          requestType = 'ui';
          break;
      }

      const resp = await axiosInstance.post('/s3/copy-eng-values', {
        key,
        type: requestType,
      });

      alert(JSON.stringify(resp.data) || 'English values copied successfully!');
      queryClient.invalidateQueries({ queryKey: ['translations'] });
      return resp.data;
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Failed to copy English values.');
    },
  });

  const { mutate: deleteKey, isPending: isDeleting } = useMutation({
    mutationFn: async ({ key, type }: { key: string; type: string }) => {
      let requestType: string;
      switch (type) {
        case 'email':
          requestType = 'email';
          break;
        case 'notifications':
          requestType = 'notification';
          break;
        case 'landing':
          requestType = 'landing';
          break;
        case 'app':
        default:
          requestType = 'ui';
          break;
      }

      const resp = await axiosInstance.delete('/s3/delete-key-from-all-languages', {
        data: {
          key,
          type: requestType,
        },
      });

      alert(JSON.stringify(resp.data) || 'Key deleted from all languages!');
      queryClient.invalidateQueries({ queryKey: ['translations'] });
      return resp.data;
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Failed to delete key.');
    },
  });

  const { mutate: copyKeyToClipboard, isPending: isCopyingToClipboard } = useMutation({
    mutationFn: async ({ section, key }: { section: string; key: string }) => {
      const allLanguages = ['en', ...languagesList];
      const values: Record<string, string> = {};

      await Promise.all(
        allLanguages.map(async (lang) => {
          try {
            const jsonData = await fetch(getLanguageJsonUrl(type, lang), {
              cache: 'no-cache',
            }).then((res) => res.json());

            const normalized =
              Object.keys(jsonData)?.[0] !== 'Common' ? { Common: jsonData } : jsonData;

            const value = normalized?.[section]?.[key];
            if (typeof value === 'string') {
              values[lang] = value;
            }
          } catch {
            // Skip languages that fail to load so the rest still copy.
          }
        })
      );

      const payload = JSON.stringify({ [key]: values }, null, 2);
      await navigator.clipboard.writeText(payload);
      return payload;
    },
    onSuccess: () => {
      alert('Copied key with all languages to clipboard!');
    },
    onError: (error: any) => {
      alert(error?.message || 'Failed to copy to clipboard.');
    },
  });

  const onAddKey = (section: string) => {
    if (newKey && !translations[section][newKey]) {
      const data = {
        ...translations,
        [section]: {
          ...translations[section],
          [newKey]: newValue,
        },
      };
      setNewKey('');
      setNewValue('');
      setTranslations(data);
    } else {
      alert('Key already exists');
    }
  };

  useEffect(() => {
    if (data) {
      setTranslations(data);
    }
  }, [data]);

  const diff = JSON.stringify(translations) !== JSON.stringify(data);
  const onSave = () => {
    if (!diff) {
      alert('No changes to save');
      return;
    }
    if (isDescriptionMode) {
      saveDescription({ translation: translations });
    } else {
      mutate({
        locale: language,
        translation: translations,
      });
    }
  };
  return (
    <div className="flex flex-1 px-4  ">
      {(isPending || isSavingDescription) && (
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-4 rounded">{isSavingDescription ? 'Saving descriptions...' : 'Loading...'}</div>
        </div>
      )}
      {isTranslating && (
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-4 rounded">Translating...</div>
        </div>
      )}
      {isCopying && (
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-4 rounded">Copying English values...</div>
        </div>
      )}
      {isDeleting && (
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-4 rounded">Deleting key...</div>
        </div>
      )}
      {isCopyingToClipboard && (
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-4 rounded">Copying to clipboard...</div>
        </div>
      )}
      {Object.keys(translations).map((section) => (
        <div key={section} className="mb-6  w-full ">
          <h2 className="text-xl fixed font-bold mb-4 bg-opacity-60 bg-white ">
            {isDescriptionMode
              ? ` Active: descriptions (${getRequestType(type)})`
              : ' Active Language: ' + language + ' ' + type}
          </h2>
          <h3 className="text-xl font-bold mb-4 mt-12">Section: {section}</h3>
          <div>
            <table className="min-w-full bg-transparent rounded mb-4">
              <thead className="bg-gray-100">
                <tr className="flex">
                  <th
                    className="py-2 px-4 text-left"
                    style={{ flex: '0 0 150px' }}
                  >
                    Key
                  </th>
                  <th className="py-2 px-4 text-left" style={{ flex: '1' }}>
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(translations[section]).map(([key, value]) => {
                  if (typeof value !== 'string') return null;
                  if (
                    search &&
                    !key.includes(search) &&
                    !value.includes(search)
                  )
                    return;

                  return (
                    <tr key={key} className="flex">
                      {language === 'en' && !isDescriptionMode && (
                        <td className="border-t flex items-center pr-2 space-x-2">
                          <button
                            style={{ whiteSpace: 'nowrap', backgroundColor: '#3b82f6', color: '#ffffff', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            onClick={() => copyEngValues({ key, type })}
                          >
                            Copy to All
                          </button>
                          <button
                            style={{ whiteSpace: 'nowrap', backgroundColor: '#22c55e', color: '#ffffff', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#16a34a'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#22c55e'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            onClick={() => translateKey({ key, type })}
                          >
                            Translate Key
                          </button>
                          <button
                            style={{ whiteSpace: 'nowrap', backgroundColor: '#ef4444', color: '#ffffff', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            onClick={() => {
                              Modal.confirm({
                                title: 'Delete Key',
                                content: `Are you sure you want to delete "${key}" from all languages?`,
                                okText: 'Yes, Delete',
                                cancelText: 'Cancel',
                                okButtonProps: { danger: true },
                                onOk: () => deleteKey({ key, type }),
                              });
                            }}
                          >
                            Delete Key
                          </button>
                          <button
                            style={{ whiteSpace: 'nowrap', backgroundColor: '#6366f1', color: '#ffffff', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#4f46e5'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#6366f1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            onClick={() => copyKeyToClipboard({ section, key })}
                          >
                            Copy to Clipboard
                          </button>
                        </td>
                      )}
                      <td
                        className="border-t flex items-center min-h-[38px]"
                        style={{ flex: '0 0 150px' }}
                      >
                        {key}
                      </td>
                      <td
                        className="border-t flex items-center"
                        style={{ flex: '1' }}
                      >
                        <input
                          className="w-full border p-1 rounded"
                          value={value}
                          onChange={(e) => {
                            setTranslations({
                              ...translations,
                              [section]: {
                                ...translations[section],
                                [key]: e.target.value,
                              },
                            });
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-4 flex space-x-2 flex-1 justify-center items-center">
              {language === 'en' && !isDescriptionMode && (
                <>
                  <input
                    className="border px-4 py-2 rounded w-1/3"
                    placeholder="New Key"
                    value={newKey}
                    onChange={(e) => {
                      setNewKey(e.target.value);
                    }}
                  />
                  <input
                    className="border px-4 py-2 rounded w-1/3"
                    placeholder="New Value"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                  />
                  <button
                    className="bg-green-500 text-black px-4 py-2 rounded whitespace-nowrap"
                    onClick={() => {
                      onAddKey(section);
                    }}
                  >
                    Add Key/Value
                  </button>
                </>
              )}

              <button
                className={`bg-green-500 text-black px-4 py-2 rounded border border-primary whitespace-nowrap ${
                  diff ? 'bg-primary !text-white' : 'bg-gray-300'
                }`}
                onClick={() => {
                  diff && onSave();
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TranslationManager;
